import React from 'react';
import { motion } from 'framer-motion';
import { SITE } from '@/constants/testIds';
import { scrollToId } from '@/lib/lenis';

/**
 * Editorial hero — restrained, senior, boutique-IB.
 * No kinetic reveal or giant outline type. Just typography, hairline rules,
 * a single supporting business-card image and one restrained gold word.
 */
export const Hero = ({ onBook }) => {
  const ease = [0.22, 1, 0.36, 1];

  return (
    <section
      id="top"
      data-testid={SITE.hero}
      className="section-dark relative pt-32 md:pt-40 pb-20 md:pb-24 px-5 md:px-10"
    >
      <div className="container-narrow">
        {/* Top meta */}
        <div className="flex items-center justify-between text-[10.5px] small-caps text-[color:var(--stone-2)]">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            Est. Mumbai — India × MENA × Hong Kong
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="hidden md:inline"
          >
            Boutique · Investment Banking · Est. 2026
          </motion.span>
        </div>

        <div className="mt-10 md:mt-12 h-px bg-[color:var(--rule-dark)]" />

        <div className="mt-14 md:mt-20 grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Left — headline */}
          <div className="col-span-12 lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35, ease }}
              className="eyebrow"
            >
              Investment Banking · M&amp;A · Strategic Advisory
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5, ease }}
              className="mt-6 font-serif text-[38px] sm:text-[52px] md:text-[64px] lg:text-[74px] leading-[1.03] tracking-[-0.02em] text-[color:var(--stone)] max-w-[900px]"
            >
              Senior-led investment banking for{' '}
              <span className="italic text-[color:var(--gold-2)]">founders</span>
              , promoters and businesses navigating capital, partnerships and
              complex transactions.
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease }}
              className="mt-10 max-w-[560px]"
            >
              <p className="text-[15px] md:text-[17px] leading-[1.6] text-[color:var(--stone-2)]">
                A boutique advisory practice serving founders, promoters,
                family offices and enterprises across India, the MENA region
                and Hong Kong. Direct partner engagement, cross-border
                execution and forty years of tier-1 pedigree — applied to
                mandates that actually close.
              </p>

              <p className="mt-6 small-caps text-[11px] text-[color:var(--gold-2)]">
                Execution Driven · Investment Banking · Unlike Everyone Else
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.05, ease }}
              className="mt-10 flex flex-wrap gap-3"
            >
              <button
                data-testid={SITE.heroCta}
                onClick={onBook}
                className="btn-primary"
              >
                Start a Conversation
                <span aria-hidden>→</span>
              </button>
              <button
                data-testid={SITE.heroSecondary}
                onClick={() => scrollToId('what')}
                className="btn-ghost"
              >
                What We Do
              </button>
            </motion.div>
          </div>

          {/* Right — meta block */}
          <div className="col-span-12 lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease }}
              className="border border-[color:var(--rule-dark-2)] rounded-sm p-6 md:p-8"
            >
              <div className="flex items-center justify-between small-caps text-[10.5px] text-[color:var(--stone-2)]">
                <span>Firm Notes</span>
                <span className="text-[color:var(--gold-2)]">01 / 04</span>
              </div>
              <div className="mt-6 space-y-6">
                <MetaRow label="Established" value="Mumbai · 2026" />
                <MetaRow label="Leadership" value="40+ years combined" />
                <MetaRow label="Regions" value="India · MENA · Hong Kong" />
                <MetaRow label="Engagement" value="Partner-level, always" />
              </div>
              <div className="mt-8 h-px bg-[color:var(--rule-dark)]" />
              <p className="mt-6 font-serif italic text-[15px] leading-[1.55] text-[color:var(--stone)]">
                “Advice you can actually take to the board.”
              </p>
              <p className="mt-2 small-caps text-[10.5px] text-[color:var(--stone-3)]">
                — Firm Philosophy
              </p>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 1.4 }}
          className="mt-20 md:mt-28 flex items-center gap-4"
        >
          <span className="small-caps text-[10.5px] text-[color:var(--stone-2)]">
            Scroll
          </span>
          <span className="block flex-1 h-px bg-[color:var(--rule-dark)]" />
          <span className="small-caps text-[10.5px] text-[color:var(--stone-2)]">
            Section 01 · Credibility
          </span>
        </motion.div>
      </div>
    </section>
  );
};

const MetaRow = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4">
    <span className="small-caps text-[10.5px] text-[color:var(--stone-3)]">
      {label}
    </span>
    <span className="font-serif text-[15px] text-[color:var(--stone)] text-right">
      {value}
    </span>
  </div>
);

export default Hero;
